import { toast } from 'sonner';
import { ApiError, ApiService } from '@/services/api';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for handling API errors with standardized toast messages
 *
 * Usage:
 * const { handleApiError } = useApiError();
 *
 * try {
 *   await apiCall();
 * } catch (error) {
 *   handleApiError(error, {
 *     defaultMessage: 'Operation failed',
 *     on401: () => navigate('/login'),
 *     on403: () => setPermissionError(true)
 *   });
 * }
 */
export const useApiError = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tApiErrors } = useTranslation('apiErrors');

  /**
   * Translated code, else the caller's own message, else the generic one.
   *
   * The API's English is deliberately not in this chain. The UI never shows a backend string:
   * an error the user should understand gets a code, and one without a code gets the caller's
   * translated fallback. That is what keeps copy out of the API and in the catalog.
   */
  const resolveErrorMessage = (
    errorCode: string | null | undefined,
    params?: Record<string, string | number>,
    callerMessage?: string | null,
  ): string => {
    if (errorCode) {
      // `defaultValue` so an untranslated code falls through instead of rendering the key.
      const translated = tApiErrors(errorCode, { ...params, defaultValue: '' });
      if (translated) return translated;
    }
    return callerMessage || tCommon('toast.error.generic');
  };

  const handleApiError = (
    error: unknown,
    options: {
      defaultMessage?: string;
      on401?: () => void;
      on403?: () => void;
      onNetworkError?: () => void;
      onError?: (error: unknown) => void;
      showToast?: boolean;
    } = {},
  ) => {
    const {
      defaultMessage = tCommon('toast.error.generic'),
      on401,
      on403,
      onNetworkError,
      onError,
      showToast = true,
    } = options;

    if (error instanceof ApiError) {
      // Handle specific API errors
      if (error.status === 401 && on401) {
        on401();
        return;
      }

      if (error.status === 403 && on403) {
        on403();
        return;
      }

      if (error.isNetworkError && onNetworkError) {
        onNetworkError();
        return;
      }

      const message = resolveErrorMessage(
        error.errorCode,
        error.errorParams,
        options.defaultMessage,
      );

      if (showToast) {
        toast.error(message);
      }

      onError?.(error);
      return message;
    } else {
      // Handle unexpected errors
      const message = error instanceof Error ? error.message : defaultMessage;
      if (showToast) {
        toast.error(message);
      }
      onError?.(error);
      return message;
    }
  };

  const handleSuccess = (message: string) => {
    toast.success(message);
  };

  const handleWarning = (message: string) => {
    toast.warning(message);
  };

  const handleInfo = (message: string) => {
    toast.info(message);
  };

  /**
   * Handle API response with error checking
   * Useful for APIs that return success responses with error fields
   */
  const handleApiResponse = <T>(
    response: T & { error?: string | null },
    options: {
      successMessage?: string;
      errorMessage?: string;
      onSuccess?: (data: T) => void;
      onError?: (error: string) => void;
    } = {},
  ) => {
    const { successMessage, errorMessage, onSuccess, onError } = options;

    if (response.error) {
      const errorMsg = resolveErrorMessage(
        ApiService.getErrorCode(response),
        ApiService.getErrorParams(response),
        errorMessage,
      );
      toast.error(errorMsg);
      onError?.(errorMsg);
      return false;
    } else {
      if (successMessage) {
        toast.success(successMessage);
      }
      onSuccess?.(response);
      return true;
    }
  };

  /**
   * Handle async operations with loading state
   */
  const handleAsyncOperation = async <T>(
    operation: () => Promise<T>,
    options: {
      loadingMessage?: string;
      successMessage?: string;
      errorMessage?: string;
      onSuccess?: (result: T) => void;
      onError?: (error: unknown) => void;
      setLoading?: (loading: boolean) => void;
    } = {},
  ) => {
    const {
      loadingMessage,
      successMessage,
      errorMessage,
      onSuccess,
      onError,
      setLoading,
    } = options;

    try {
      setLoading?.(true);
      if (loadingMessage !== undefined) {
        const loadingToastMessage =
          loadingMessage || tCommon('toast.loading.generic');
        toast.loading(loadingToastMessage);
      }

      const result = await operation();

      if (successMessage) {
        handleSuccess(successMessage);
      }
      onSuccess?.(result);
      return result;
    } catch (error) {
      handleApiError(error, {
        defaultMessage: errorMessage || tCommon('toast.error.operationFailed'),
        onError,
      });
      throw error;
    } finally {
      setLoading?.(false);
    }
  };

  /**
   * Handle API responses that might contain errors in the response body
   * Useful for APIs that return success responses with error fields
   */
  const handleApiResponseWithErrorCheck = <T>(
    response: T,
    options: {
      successMessage?: string;
      errorMessage?: string;
      onSuccess?: (data: T) => void;
      onError?: (error: string) => void;
      checkForErrors?: boolean;
    } = {},
  ) => {
    const {
      successMessage,
      errorMessage,
      onSuccess,
      onError,
      checkForErrors = true,
    } = options;

    if (checkForErrors && ApiService.hasErrorInResponse(response)) {
      const errorMsg = resolveErrorMessage(
        ApiService.getErrorCode(response),
        ApiService.getErrorParams(response),
        errorMessage,
      );
      toast.error(errorMsg);
      onError?.(errorMsg);
      return { success: false, error: errorMsg };
    } else {
      if (successMessage) {
        toast.success(successMessage);
      }
      onSuccess?.(response);
      return { success: true, data: response };
    }
  };

  /**
   * Handle API calls with flexible error handling
   * Allows developers to choose between automatic error handling or manual control
   */
  const handleApiCall = async <T>(
    apiCall: () => Promise<T>,
    options: {
      autoHandleErrors?: boolean;
      successMessage?: string;
      errorMessage?: string;
      onSuccess?: (result: T) => void;
      onError?: (error: unknown) => void;
      setLoading?: (loading: boolean) => void;
    } = {},
  ) => {
    const {
      autoHandleErrors = true,
      successMessage,
      errorMessage,
      onSuccess,
      onError,
      setLoading,
    } = options;

    try {
      setLoading?.(true);
      const result = await apiCall();

      if (autoHandleErrors) {
        // Check if response contains errors
        if (ApiService.hasErrorInResponse(result)) {
          const errorMsg = resolveErrorMessage(
            ApiService.getErrorCode(result),
            ApiService.getErrorParams(result),
            errorMessage,
          );
          toast.error(errorMsg);
          onError?.(ApiService.createErrorFromResponse(result));
          return { success: false, error: errorMsg };
        }
      }

      if (successMessage) {
        handleSuccess(successMessage);
      }
      onSuccess?.(result);
      return { success: true, data: result };
    } catch (error) {
      if (autoHandleErrors) {
        handleApiError(error, {
          defaultMessage:
            errorMessage || tCommon('toast.error.operationFailed'),
          onError,
        });
      }
      throw error;
    } finally {
      setLoading?.(false);
    }
  };

  return {
    handleApiError,
    handleSuccess,
    handleWarning,
    handleInfo,
    handleApiResponse,
    handleAsyncOperation,
    handleApiResponseWithErrorCheck,
    handleApiCall,
  };
};
